import { useEffect, useState } from 'react';

import ContentTypeChooser from './ContentTypeChooser';
import InteractiveImageContentForm from './form-types/interactive-image-content-form/InteractiveImageContentForm';
import PdfContentForm from './form-types/PdfContentForm';
import QuizContentForm from './form-types/quiz-content-form/QuizContentForm';
import SelectSurveyContentForm from './form-types/SelectSurveyContentForm';
import TextSurveyContentForm from './form-types/survey-content-form/TextSurveyContentForm';
import TextContentForm from './form-types/TextContentForm';
import VideoContentForm from './form-types/video-content-form/VideoContentForm';
import {
  ContentData,
  ContentSpecificData,
  ContentType,
  InteractiveImageContentData,
  QuizContentData,
  TextSurveyContentData,
  TextContentData,
  VideoContentData,
  SelectSurveyContentData,
  PdfContentData,
} from '../../../types/contentTypes';

export interface ContentFormProps<T extends ContentData, U extends ContentSpecificData> {
  content?: T;
  headline?: string;
  onNewContentSubmit?: (props: U & { headline: string }) => void;
  onExistingContentSubmit?: (content: T) => void;
}

interface ContentEditorProps {
  content?: ContentData;
  onNewContentSubmit?: (content: ContentSpecificData & { headline: string }, tpe: ContentType) => void;
  onExistingContentSubmit?: (content: ContentData) => void;
  onNewTypeSelect?: () => void;
  onDelete?: () => void;
  className?: string;
  id?: string;
  scroll?: boolean;
}

export default function ContentEditor({
  content,
  onNewContentSubmit,
  onExistingContentSubmit,
  onNewTypeSelect,
  onDelete,
  className = '',
  id = '',
  scroll = false,
}: ContentEditorProps) {
  const [formType, setFormType] = useState<ContentType | undefined>(content?.type);

  function handleSelect(tpe?: ContentType) {
    if (tpe !== formType && tpe !== undefined) {
      onNewTypeSelect?.();
    }
    setFormType(tpe);
  }

  useEffect(() => {
    function scrollComponentIntoView() {
      if (id === undefined || !scroll) return;
      const current = document.getElementById(id);
      current?.scrollIntoView({ behavior: 'smooth' });
    }

    scrollComponentIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We're only interested in the initial value of scroll, not the updates.
  }, [formType, id]);

  return (
    <div id={id} className={`content-editor ${className}`}>
      <ContentTypeChooser
        className="mb-8"
        value={formType}
        onSelect={handleSelect}
        onDelete={onDelete}
        disabled={content && 'id' in content}
      />
      {formType === ContentType.Text && (
        <TextContentForm
          content={content as TextContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.Video && (
        <VideoContentForm
          content={content as VideoContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.Quiz && (
        <QuizContentForm
          content={content as QuizContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.InteractiveImage && (
        <InteractiveImageContentForm
          content={content as InteractiveImageContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.TextSurvey && (
        <TextSurveyContentForm
          content={content as TextSurveyContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.SelectSurvey && (
        <SelectSurveyContentForm
          content={content as SelectSurveyContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
      {formType === ContentType.Pdf && (
        <PdfContentForm
          content={content as PdfContentData}
          onNewContentSubmit={props => onNewContentSubmit?.(props, formType)}
          onExistingContentSubmit={onExistingContentSubmit}
        />
      )}
    </div>
  );
}
